import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { communityService } from '../api/services';
import useApi from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import {
  Users, Plus, MessageCircle, Search, Loader2,
  AlertCircle, Video, Crown, X,
} from 'lucide-react';
import toast from 'react-hot-toast';

// ── Create Community Modal ────────────────────────────────────
const CreateModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({ name: '', description: '', imageUrl: '' });
  const [saving, setSaving] = useState(false);
  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const res = await communityService.create(form);
      const community = res.data?.data?.community ?? res.data?.community;
      toast.success('Community created!');
      onCreated(community);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not create community');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-forest-900/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-xl font-semibold text-forest-900">Create Community</h3>
          <button onClick={onClose} className="text-forest-400 hover:text-forest-700"><X size={18} /></button>
        </div>

        <div>
          <label className="block text-xs font-medium text-forest-700 mb-1.5">Community Name *</label>
          <input value={form.name} onChange={f('name')} placeholder="e.g. Eco-Tourism Entrepreneurs"
            className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white" />
        </div>
        <div>
          <label className="block text-xs font-medium text-forest-700 mb-1.5">Description <span className="text-forest-400">(optional)</span></label>
          <textarea value={form.description} onChange={f('description')} rows={3}
            placeholder="What is this community about?"
            className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white resize-none" />
        </div>
        <div>
          <label className="block text-xs font-medium text-forest-700 mb-1.5">Group Image URL <span className="text-forest-400">(optional)</span></label>
          <input value={form.imageUrl} onChange={f('imageUrl')} placeholder="https://..."
            className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white" />
        </div>

        <button onClick={handleCreate} disabled={saving}
          className="btn-primary w-full justify-center flex items-center gap-2 disabled:opacity-60">
          {saving ? <><Loader2 size={14} className="animate-spin" /> Creating...</> : <><Users size={14} /> Create Community</>}
        </button>
      </div>
    </div>
  );
};

// ── Community Card ────────────────────────────────────────────
const CommunityCard = ({ community, currentUserId }) => {
  const lastMsg  = community.messages?.[0];
  const isAdmin  = community.createdBy === currentUserId;
  const members  = community._count?.members ?? community.members?.length ?? 0;

  return (
    <Link to={`/communities/${community.id}`}
      className="bg-white border border-cream-200 rounded-2xl p-4 flex items-center gap-4 hover:shadow-sm hover:border-forest-200 transition-all group">

      {/* Avatar */}
      <div className="w-14 h-14 rounded-2xl flex-shrink-0 overflow-hidden border border-cream-200">
        {community.imageUrl ? (
          <img src={community.imageUrl} alt={community.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-forest-700 flex items-center justify-center">
            <span className="font-serif font-bold text-cream-50 text-xl">{community.name[0]}</span>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-semibold text-forest-900 text-sm truncate">{community.name}</p>
          {isAdmin && <Crown size={12} className="text-amber-500 flex-shrink-0" />}
        </div>
        <p className="text-xs text-forest-400 mt-0.5 flex items-center gap-1">
          <Users size={10} /> {members} member{members !== 1 ? 's' : ''}
        </p>
        {lastMsg ? (
          <p className="text-xs text-forest-500 mt-1 truncate">
            <span className="font-medium">{lastMsg.sender?.firstName}:</span> {lastMsg.content}
          </p>
        ) : (
          <p className="text-xs text-forest-300 mt-1 italic">No messages yet</p>
        )}
      </div>

      {/* Unread / arrow */}
      <div className="flex-shrink-0 text-forest-300 group-hover:text-forest-600 transition-colors">
        <MessageCircle size={18} />
      </div>
    </Link>
  );
};

// ── Main Page ─────────────────────────────────────────────────
const CommunitiesPage = () => {
  const { user }        = useAuth();
  const navigate        = useNavigate();
  const [showCreate,    setShowCreate]    = useState(false);
  const [refetch,       setRefetch]       = useState(0);

  const { data, loading, error } = useApi(
    () => communityService.getMy(), [refetch]
  );

  const communities = Array.isArray(data?.data?.communities) ? data.data.communities
    : Array.isArray(data?.communities) ? data.communities
    : Array.isArray(data?.data) ? data.data : [];

  return (
    <DashboardLayout title="Communities">
      <div className="max-w-2xl space-y-5">

        {/* Header */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-forest-500">
            Group chats and video calls with fellow entrepreneurs and experts.
          </p>
          <button onClick={() => setShowCreate(true)}
            className="btn-primary flex items-center gap-2 text-sm flex-shrink-0">
            <Plus size={14} /> New Community
          </button>
        </div>

        {/* Info banner */}
        <div className="bg-forest-50 border border-forest-100 rounded-2xl px-4 py-3 flex items-start gap-3">
          <Users size={16} className="text-forest-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-forest-700 leading-relaxed">
            Create or join a community to chat with other entrepreneurs and experts.
            You can also start a <strong>group video call</strong> directly from inside any community.
          </p>
        </div>

        {error && (
          <div className="flex items-center gap-3 text-rust-500 bg-rust-50 border border-rust-200 rounded-xl px-4 py-3">
            <AlertCircle size={16} /> Could not load communities.
          </div>
        )}

        {loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-20 bg-white border border-cream-200 rounded-2xl animate-pulse" />
          ))
        ) : communities.length === 0 ? (
          <div className="text-center py-16 bg-white border border-cream-200 rounded-2xl text-forest-400">
            <Users size={40} className="mx-auto mb-4 opacity-30" />
            <p className="font-serif text-xl text-forest-600">No communities yet</p>
            <p className="text-sm mt-2 mb-6">Create one or wait to be invited by someone.</p>
            <button onClick={() => setShowCreate(true)} className="btn-primary inline-flex items-center gap-2">
              <Plus size={14} /> Create Community
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {communities.map(c => (
              <CommunityCard key={c.id} community={c} currentUserId={user?.id} />
            ))}
          </div>
        )}
      </div>

      {showCreate && (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onCreated={(c) => { setShowCreate(false); setRefetch(r => r + 1); navigate(`/communities/${c.id}`); }}
        />
      )}
    </DashboardLayout>
  );
};

export default CommunitiesPage;